/**
 * THE WIRE Report - Pitch Tipping Intelligence Dashboard
 *
 * Full-featured opponent scouting report with:
 * - Video thumbnails from GCS bucket
 * - Pitcher selector
 * - Hitter/Coach view toggle
 * - Pitch type breakdown
 * - Critical tells with validation
 * - Video gallery with pagination
 * - Recommendations panel
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import {
  ArrowLeft,
  Play,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Zap,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  User,
  Users,
  FileDown,
  FileText,
  Download,
  Share2,
  Copy,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Brain,
  Activity,
} from 'lucide-react';

import {
  loadWireSummary,
  loadPitcherDetail,
  getVideoUrl,
  generateHitterScoutingReport,
} from '../../lib/wireDataLoader';

import {
  getConfidenceLevel,
  getConfidenceColor,
  PITCH_TYPE_LABELS,
  TIGERS_PITCHERS,
  COLLEGE_PITCHERS,
  INTEL_SOURCES,
  getTippingRiskLevel,
} from '../../lib/wireTypes';

// New 2026 visualization components
import CircularGauge, { ScoreTriad, CompactGauge } from './CircularGauge';
import TippingRadarChart, { TippingRadarLegend, CompactRadar } from './TippingRadarChart';
import ColorCodedMetricsTable, { PitchTypeScoreCard } from './ColorCodedMetricsTable';
import StrikeZoneHeatmap, { DualZoneDisplay } from './StrikeZoneHeatmap';
import { BentoCard, BentoStat, ResponsiveBentoGrid, GlassCard, StatRow, BentoSection, BentoHeader } from './BentoGrid';
import VideoViewerWithMarkers from './VideoViewerWithMarkers';
import PitcherChat from './PitcherChat';
import HitterIntelBriefing from './HitterIntelBriefing';
import SlamAIEvaluation from './SlamAIEvaluation';
import { TellCarousel } from './TellCarousel';

// =============================================================================
// VIDEO THUMBNAIL COMPONENT
// =============================================================================

function VideoThumbnail({ analysis, onClick, hasPublicVideos = true }) {
  const level = getConfidenceLevel(analysis.confidence);
  const color = getConfidenceColor(level);
  const pitchType = analysis.pitchType || 'UNK';
  const videoRef = useRef(null);
  const [thumbnailReady, setThumbnailReady] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);

  // Skip video loading if source doesn't have public video access
  const videoUrl = hasPublicVideos && analysis.gcsUri ? getVideoUrl(analysis.gcsUri) : null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    const handleLoadedMetadata = () => {
      video.currentTime = 0.5;
    };

    const handleSeeked = () => {
      setThumbnailReady(true);
    };

    const handleError = () => {
      setThumbnailError(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl]);

  return (
    <button
      onClick={onClick}
      className="group relative bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-all w-full text-left"
    >
      <div className="aspect-video bg-zinc-800 relative flex items-center justify-center overflow-hidden">
        {videoUrl && !thumbnailError && (
          <video
            ref={videoRef}
            src={videoUrl}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
              thumbnailReady ? 'opacity-100' : 'opacity-0'
            }`}
            muted
            playsInline
            preload="metadata"
          />
        )}

        {(!thumbnailReady || thumbnailError) && (
          <div className="absolute inset-0 bg-zinc-800 flex items-center justify-center">
            {thumbnailError ? (
              <div className="text-zinc-600 text-xs">No preview</div>
            ) : (
              <div className="w-6 h-6 rounded-full border-2 border-zinc-600 border-t-zinc-400 animate-spin" />
            )}
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
          </div>
        </div>

        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur rounded text-xs font-mono text-white">
          {pitchType}
        </div>

        {analysis.tippingDetected && (
          <div
            className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: color }}
            title={`${level} confidence tipping detected`}
          />
        )}
      </div>

      <div className="p-2 flex items-center justify-between">
        <span className="text-xs text-zinc-400">{analysis.gameDate}</span>
        {analysis.tippingDetected && (
          <span
            className="text-xs px-1.5 py-0.5 rounded font-mono"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {(analysis.confidence * 100).toFixed(0)}%
          </span>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// VIDEO GALLERY COMPONENT
// =============================================================================

function VideoGallery({ analyses, maxDisplay = 8, pageSize = 12, hasPublicVideos = true, pitcherName = '', onAnalysisUpdated }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [viewMode, setViewMode] = useState('paginated');
  const [approvedVideos, setApprovedVideos] = useState(new Set());
  const [rejectedVideos, setRejectedVideos] = useState(new Set());

  const sortedAnalyses = useMemo(() =>
    [...analyses].sort((a, b) => {
      if (a.tippingDetected !== b.tippingDetected) {
        return a.tippingDetected ? -1 : 1;
      }
      return b.confidence - a.confidence;
    }),
    [analyses]
  );

  const totalPages = Math.ceil(sortedAnalyses.length / pageSize);
  const startIdx = currentPage * pageSize;
  const endIdx = Math.min(startIdx + pageSize, sortedAnalyses.length);
  const displayedAnalyses = viewMode === 'compact'
    ? sortedAnalyses.slice(0, maxDisplay)
    : sortedAnalyses.slice(startIdx, endIdx);

  const hasMore = analyses.length > maxDisplay;
  const tippingCount = analyses.filter(a => a.tippingDetected).length;

  const navigateVideo = (direction) => {
    if (!selectedVideo) return;
    const currentIdx = sortedAnalyses.findIndex(a => a.videoUid === selectedVideo.videoUid);
    if (direction === 'prev' && currentIdx > 0) {
      setSelectedVideo(sortedAnalyses[currentIdx - 1]);
    } else if (direction === 'next' && currentIdx < sortedAnalyses.length - 1) {
      setSelectedVideo(sortedAnalyses[currentIdx + 1]);
    }
  };

  // Handle video approval - mark as approved and advance to next
  const handleApprove = (analysis) => {
    setApprovedVideos(prev => new Set([...prev, analysis.videoUid]));
    // Navigate to next video or close if at end
    const currentIdx = sortedAnalyses.findIndex(a => a.videoUid === analysis.videoUid);
    if (currentIdx < sortedAnalyses.length - 1) {
      setSelectedVideo(sortedAnalyses[currentIdx + 1]);
    } else {
      setSelectedVideo(null); // Close modal if at end
    }
  };

  // Handle video rejection
  const handleReject = (analysis) => {
    setRejectedVideos(prev => new Set([...prev, analysis.videoUid]));
  };

  // Handle reanalyze - call backend API
  const handleReanalyze = async (analysis, instructions) => {
    try {
      const response = await fetch('/api/wire/video/reanalyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUid: analysis.videoUid,
          gcsUri: analysis.gcsUri,
          pitcherName: pitcherName,
          pitchType: analysis.pitchTypeGroundTruth || analysis.pitchType,
          gameDate: analysis.gameDate,
          existingTells: analysis.indicators || [],
          userInstructions: instructions
        })
      });

      if (!response.ok) {
        throw new Error('Reanalysis failed');
      }

      const data = await response.json();
      return data.analysis;
    } catch (error) {
      console.error('Reanalyze error:', error);
      throw error;
    }
  };

  // Handle when analysis is updated after reanalyze
  const handleAnalysisUpdated = (newAnalysis) => {
    setSelectedVideo(newAnalysis);
    onAnalysisUpdated?.(newAnalysis);
  };

  // Get current video index
  const getCurrentVideoIndex = () => {
    if (!selectedVideo) return 0;
    return sortedAnalyses.findIndex(a => a.videoUid === selectedVideo.videoUid) + 1;
  };

  return (
    <div className="bg-zinc-800/30 border border-zinc-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-400" />
            Video Evidence
          </h3>
          <span className="text-sm text-zinc-400">
            {tippingCount} of {analyses.length} with tells
          </span>
          {approvedVideos.size > 0 && (
            <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
              {approvedVideos.size} approved
            </span>
          )}
        </div>

        {hasMore && (
          <button
            onClick={() => {
              setViewMode(viewMode === 'compact' ? 'paginated' : 'compact');
              setCurrentPage(0);
            }}
            className="text-sm text-orange-400 hover:underline"
          >
            {viewMode === 'compact' ? `Browse all ${analyses.length}` : 'Show less'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {displayedAnalyses.map((analysis) => (
          <VideoThumbnail
            key={analysis.videoUid}
            analysis={analysis}
            onClick={() => setSelectedVideo(analysis)}
            hasPublicVideos={hasPublicVideos}
          />
        ))}
      </div>

      {viewMode === 'paginated' && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
            className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="text-sm text-zinc-400 px-3">
            {startIdx + 1}-{endIdx} of {sortedAnalyses.length}
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
            className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Video Modal - Compact with Navigation inside Player */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Video Player with integrated navigation and feedback */}
            {hasPublicVideos ? (
              <VideoViewerWithMarkers
                key={selectedVideo.videoUid || selectedVideo.gcsUri}
                analysis={selectedVideo}
                onClose={() => setSelectedVideo(null)}
                onNavigatePrev={getCurrentVideoIndex() > 1 ? () => navigateVideo('prev') : undefined}
                onNavigateNext={getCurrentVideoIndex() < sortedAnalyses.length ? () => navigateVideo('next') : undefined}
                onApprove={handleApprove}
                onReject={handleReject}
                onReanalyze={handleReanalyze}
                onAnalysisUpdated={handleAnalysisUpdated}
                currentIndex={getCurrentVideoIndex()}
                totalCount={sortedAnalyses.length}
              />
            ) : (
              <div className="aspect-video bg-zinc-800 rounded-lg flex flex-col items-center justify-center text-zinc-400">
                <Play className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm font-medium">Video preview unavailable</p>
                <p className="text-xs mt-1 opacity-75">Pitch analysis data shown below</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PITCHER SELECTOR COMPONENT
// =============================================================================

function PitcherSelector({ pitchers, selected, onSelect, summaryData }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {pitchers.map((pitcher) => {
        const data = summaryData?.[pitcher];
        const isSelected = selected === pitcher;
        const tippingColor = data?.tippingRate >= 0.25 ? '#f97316' : data?.tippingRate >= 0.15 ? '#eab308' : '#22c55e';

        return (
          <button
            key={pitcher}
            onClick={() => onSelect(pitcher)}
            className={`flex-shrink-0 px-4 py-3 rounded-xl border transition-all ${
              isSelected
                ? 'bg-orange-500/20 border-orange-500/50 text-white'
                : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600'
            }`}
          >
            <div className="text-left">
              <div className="font-semibold">{pitcher}</div>
              {data && (
                <div className="flex items-center gap-3 mt-1 text-xs">
                  <span className="text-zinc-400">{data.totalPitches} pitches</span>
                  <span style={{ color: tippingColor }}>
                    {(data.tippingRate * 100).toFixed(0)}% tipping
                  </span>
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// VIEW TOGGLE COMPONENT
// =============================================================================

function ViewToggle({ value, onChange }) {
  return (
    <div className="flex bg-zinc-800 rounded-lg p-1">
      <button
        onClick={() => onChange('coach')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
          value === 'coach'
            ? 'bg-orange-500 text-white'
            : 'text-zinc-400 hover:text-white'
        }`}
      >
        <Users className="w-4 h-4" />
        Coach
      </button>
      <button
        onClick={() => onChange('hitter')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
          value === 'hitter'
            ? 'bg-orange-500 text-white'
            : 'text-zinc-400 hover:text-white'
        }`}
      >
        <User className="w-4 h-4" />
        Hitter
      </button>
    </div>
  );
}

// =============================================================================
// PITCHER SUMMARY CARD - 2026 Redesign
// =============================================================================

function PitcherSummaryCard({ pitcher, summary, viewMode }) {
  // Check both MLB and College pitcher registries
  const info = TIGERS_PITCHERS[pitcher] || COLLEGE_PITCHERS[pitcher];
  const tippingRiskLevel = getTippingRiskLevel(summary.tippingRate);
  const tippingRate = summary.tippingRate * 100;
  const avgConfidence = summary.avgConfidence * 100;

  // Note: Radar chart removed - was using fabricated data from keyword matching
  // Real pattern analysis would require actual biomechanical tracking data

  return (
    <GlassCard className="p-6" opacity={60}>
      {/* Top Section - Profile and Gauges */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        {/* Pitcher Profile */}
        <div className="flex items-center gap-4 lg:w-1/3">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-zinc-700 flex items-center justify-center overflow-hidden ring-2 ring-zinc-600">
              {info?.imageUrl ? (
                <img
                  src={info.imageUrl}
                  alt={info?.name || pitcher}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <User className="w-10 h-10 text-zinc-500" />
              )}
            </div>
            {/* Risk indicator badge */}
            <div
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 border-zinc-900"
              style={{ backgroundColor: getConfidenceColor(tippingRiskLevel), color: '#fff' }}
            >
              {tippingRiskLevel === 'CRITICAL' ? '!' : tippingRiskLevel.charAt(0)}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white tracking-tight">{summary.fullName || info?.name || pitcher}</h2>
            <p className="text-zinc-400 text-sm">
              {summary.team || info?.team} &bull; {summary.position || (info?.throws + 'HP')}
            </p>
            <div className="flex items-center gap-2 mt-2">
              {(() => {
                // Handle arsenal as either string or array
                const arsenal = summary.arsenal || info?.arsenal;
                if (!arsenal) return null;
                const pitches = Array.isArray(arsenal) ? arsenal : arsenal.split('/');
                return pitches.map((pitch, idx) => (
                  <span key={idx} className="px-2 py-0.5 text-xs bg-zinc-800 text-zinc-300 rounded-full font-mono">
                    {pitch.trim()}
                  </span>
                ));
              })()}
            </div>
          </div>
        </div>

        {/* Score Gauges - Composite Style */}
        <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-6 lg:gap-8">
          <CircularGauge
            value={tippingRate}
            maxValue={100}
            label="Tipping Rate"
            sublabel="Overall"
            size="lg"
            inverted={true}
          />
          <div className="hidden sm:block w-px h-24 bg-zinc-700" />
          <CircularGauge
            value={avgConfidence}
            maxValue={100}
            label="Confidence"
            sublabel="Avg"
            size="md"
            inverted={true}
          />
          <div className="hidden sm:block w-px h-24 bg-zinc-700" />
          {/* Critical Tells - show actual count */}
          <div className="flex flex-col items-center">
            <div className="text-4xl font-bold text-red-400 font-mono">
              {summary.criticalTells.length}
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
              Critical Tells
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Identified
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row - Detailed Metrics */}
      <div className="mt-6 pt-6 border-t border-zinc-700/50">
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <BentoStat
            label="Pitches"
            value={summary.totalPitches}
            subValue={`${summary.analyses?.length || 0} analyzed`}
            size="sm"
          />
          <BentoStat
            label="Tipping Detected"
            value={summary.tippingDetectedCount}
            subValue={`${((summary.tippingDetectedCount / summary.totalPitches) * 100).toFixed(1)}%`}
            valueColor="#f97316"
            size="sm"
          />
          <BentoStat
            label="Clean Pitches"
            value={summary.totalPitches - summary.tippingDetectedCount}
            valueColor="#22c55e"
            size="sm"
          />
          <BentoStat
            label="High Confidence"
            value={summary.analyses?.filter(a => a.confidence > 0.7).length || 0}
            valueColor="#ef4444"
            size="sm"
          />
          <div className="col-span-2 hidden lg:block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Risk Assessment</span>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${tippingRate}%`,
                    backgroundColor: getConfidenceColor(tippingRiskLevel)
                  }}
                />
              </div>
              <span className="text-xs font-mono" style={{ color: getConfidenceColor(tippingRiskLevel) }}>
                {tippingRiskLevel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Take Alert - Enhanced */}
      {viewMode === 'hitter' && summary.criticalTells.length > 0 && (
        <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-orange-400">Quick Take</span>
                <span className="px-2 py-0.5 text-[10px] bg-orange-500/20 text-orange-300 rounded-full uppercase tracking-wider">
                  Exploitable
                </span>
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">
                <strong>{summary.criticalTells[0]?.tell}</strong> detected with{' '}
                <span className="font-mono text-orange-400">{(summary.criticalTells[0]?.confidence * 100).toFixed(0)}%</span> reliability.
                {' '}{summary.recommendations[0]}
              </p>
            </div>
            <CompactGauge value={summary.criticalTells[0]?.confidence * 100 || 0} inverted={true} size={36} />
          </div>
        </div>
      )}
    </GlassCard>
  );
}

function StatBox({ label, value, valueColor, subValue }) {
  return (
    <div className="p-3 bg-zinc-900/50 rounded-lg min-h-[90px] flex flex-col justify-between">
      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1 truncate" title={label}>{label}</p>
      <p
        className="text-xl sm:text-2xl font-bold font-mono truncate"
        style={{ color: valueColor || 'white' }}
        title={value}
      >
        {value}
      </p>
      {subValue && (
        <p className="text-xs text-zinc-500 mt-0.5 truncate" title={subValue}>{subValue}</p>
      )}
    </div>
  );
}

// =============================================================================
// PITCH TYPE BREAKDOWN - 2026 Redesign with Score Cards
// =============================================================================

function PitchTypeBreakdown({ byPitchType, viewMode, analyses }) {
  const pitchTypes = Object.entries(byPitchType || {});

  if (pitchTypes.length === 0) {
    return null;
  }

  // Calculate zone data per pitch type for heatmaps
  const getZoneDataForPitch = (pitchTypeKey) => {
    const pitchAnalyses = analyses?.filter(a => a.pitchType === pitchTypeKey) || [];
    // Generate mock zone data based on tipping distribution
    const zones = {};
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const count = pitchAnalyses.filter(a => {
          // Distribute based on some pseudo-location logic
          const hash = (a.videoUid?.charCodeAt(0) || 0) % 9;
          return hash === (row * 3 + col);
        }).length;
        const tipping = pitchAnalyses.filter(a => {
          const hash = (a.videoUid?.charCodeAt(0) || 0) % 9;
          return hash === (row * 3 + col) && a.tippingDetected;
        }).length;
        zones[`${row}-${col}`] = count > 0 ? (tipping / count) * 100 : 0;
      }
    }
    return zones;
  };

  return (
    <GlassCard className="p-6" opacity={50}>
      <BentoHeader
        title="Pitch Arsenal Analysis"
        subtitle={`${pitchTypes.length} pitch types analyzed`}
        icon={<TrendingUp className="w-5 h-5 text-blue-400" />}
      />

      {/* Pitch Type Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {pitchTypes.map(([type, stats]) => {
          const tippingRate = (stats.tippingRate || 0) * 100;
          const zoneData = getZoneDataForPitch(type);

          return (
            <div
              key={type}
              className="group relative bg-zinc-800/70 border border-zinc-700 rounded-xl p-4 hover:border-zinc-500 hover:bg-zinc-800 transition-all duration-300"
            >
              {/* Header with pitch type and score */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors">
                    {PITCH_TYPE_LABELS[type] || type}
                  </h4>
                  <p className="text-xs text-zinc-500 font-mono">{type}</p>
                </div>
                <CircularGauge
                  value={tippingRate}
                  maxValue={100}
                  size="sm"
                  inverted={true}
                  showPercentage={true}
                />
              </div>

              {/* Progress Bar */}
              <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${tippingRate}%`,
                    backgroundColor: tippingRate > 50 ? '#ef4444' : tippingRate > 25 ? '#f97316' : tippingRate > 10 ? '#eab308' : '#22c55e'
                  }}
                />
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                  <p className="text-xl font-bold font-mono text-white">{stats.totalCount || stats.count}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Pitches</p>
                </div>
                <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                  <p className="text-xl font-bold font-mono text-orange-400">{stats.tippingDetectedCount || stats.tippingCount}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Tipping</p>
                </div>
                <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
                  <p className="text-xl font-bold font-mono text-green-400">{(stats.totalCount || stats.count) - (stats.tippingDetectedCount || stats.tippingCount)}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Clean</p>
                </div>
              </div>

              {/* Mini Zone Heatmap (coach view) */}
              {viewMode === 'coach' && (
                <div className="flex items-center justify-between pt-3 border-t border-zinc-700/50">
                  <div className="flex-1">
                    <p className="text-[10px] text-zinc-500 uppercase mb-1">Avg Confidence</p>
                    <p className="text-sm font-mono font-bold text-zinc-300">
                      {((stats.avgConfidence || 0) * 100).toFixed(0)}%
                    </p>
                  </div>
                  <StrikeZoneHeatmap
                    data={{ zones: zoneData }}
                    size={60}
                    metric="tipping"
                    showValues={false}
                    showBorder={false}
                    title=""
                  />
                </div>
              )}

              {/* Common Tells */}
              {stats.commonIndicators?.length > 0 && (
                <div className="pt-3 border-t border-zinc-700/50">
                  <p className="text-[10px] text-zinc-500 uppercase mb-2">Common Tells</p>
                  <div className="flex flex-wrap gap-1">
                    {stats.commonIndicators.slice(0, 2).map((tell, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 rounded-lg bg-orange-500/10 text-orange-300 border border-orange-500/20"
                      >
                        {tell.length > 20 ? tell.substring(0, 20) + '...' : tell}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

// =============================================================================
// CRITICAL TELLS PANEL (with individual feedback per tell)
// =============================================================================

function CriticalTellsPanel({ criticalTells, viewMode, onTellFeedback }) {
  const [expandedTells, setExpandedTells] = useState({});
  const [tellFeedback, setTellFeedback] = useState({}); // { tellId: 'helpful' | 'not_helpful' | 'dismissed' }

  const toggleTell = (idx) => {
    setExpandedTells(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleTellFeedback = (tellIdx, tell, feedbackType) => {
    setTellFeedback(prev => ({
      ...prev,
      [tellIdx]: feedbackType
    }));

    // Call parent callback to send feedback to backend
    onTellFeedback?.({
      tellId: tell.tell,
      tellData: tell,
      feedback: feedbackType,
      timestamp: new Date().toISOString(),
      // If 'not_helpful', this tell should be omitted from future analysis
      omitFromFuture: feedbackType === 'not_helpful'
    });
  };

  // Filter out dismissed tells
  const visibleTells = criticalTells.filter((_, idx) => tellFeedback[idx] !== 'dismissed');

  // Coach mode gets enhanced styling
  const isCoachMode = viewMode === 'coach';

  return (
    <div className={`relative rounded-2xl overflow-hidden backdrop-blur-md ${
      isCoachMode
        ? 'border border-orange-500/20 bg-gradient-to-br from-zinc-800/60 to-zinc-900/60'
        : 'border border-zinc-700 bg-zinc-800/30'
    }`}>
      {/* Coach mode ambient glow */}
      {isCoachMode && (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-red-500/5 pointer-events-none" />
      )}

      <div className="relative p-4">
        <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${
          isCoachMode ? 'text-orange-400' : 'text-white'
        }`}>
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            isCoachMode ? 'bg-orange-500/20 ring-1 ring-orange-500/30' : 'bg-zinc-700/50'
          }`}>
            <Zap className={`w-4 h-4 ${isCoachMode ? 'text-orange-400' : 'text-orange-400'}`} />
          </div>
          Critical Tells
          <span className={`ml-auto text-xs font-mono px-2 py-0.5 rounded-full ${
            isCoachMode ? 'bg-orange-500/20 text-orange-300' : 'bg-zinc-700 text-zinc-400'
          }`}>
            {visibleTells.length}
          </span>
        </h3>

      {visibleTells.length === 0 ? (
        <p className="text-zinc-500 text-sm">No critical tells identified.</p>
      ) : (
        <div className="space-y-2 max-h-[320px] lg:max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
          {criticalTells.map((tell, idx) => {
            // Skip dismissed tells
            if (tellFeedback[idx] === 'dismissed') return null;

            const feedback = tellFeedback[idx];

            return (
              <div key={idx} className={`rounded-xl p-3 transition-all ${
                isCoachMode
                  ? 'bg-zinc-800/40 border border-white/5 hover:border-orange-500/30'
                  : 'bg-zinc-800/50 border border-zinc-700'
              }`}>
                <div
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => toggleTell(idx)}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isCoachMode ? 'bg-orange-500/20 ring-1 ring-orange-500/20' : 'bg-orange-500/20'
                    }`}>
                      <Zap className="w-3 h-3 text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold text-white leading-tight">{tell.tell}</h4>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          isCoachMode ? 'bg-zinc-700/50 text-zinc-300' : 'bg-zinc-700 text-zinc-300'
                        }`}>
                          {tell.pitchTypesAffected?.join(', ') || 'All'}
                        </span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                          style={{
                            backgroundColor: `${getConfidenceColor(getConfidenceLevel(tell.confidence))}20`,
                            color: getConfidenceColor(getConfidenceLevel(tell.confidence))
                          }}
                        >
                          {(tell.confidence * 100).toFixed(0)}%
                        </span>
                        {/* Show feedback status */}
                        {feedback === 'helpful' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5">
                            <CheckCircle className="w-2.5 h-2.5" /> OK
                          </span>
                        )}
                        {feedback === 'not_helpful' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 flex items-center gap-0.5">
                            <AlertTriangle className="w-2.5 h-2.5" /> Flagged
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedTells[idx] ? (
                    <ChevronUp className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-zinc-500 flex-shrink-0" />
                  )}
                </div>

                {expandedTells[idx] && (
                  <div className="mt-2 pt-2 border-t border-white/5">
                    <p className="text-[11px] text-zinc-400 leading-relaxed">{tell.description}</p>

                    {/* Enhanced Coach Mode Exploitability Metrics */}
                    {isCoachMode && tell.exploitability && (
                      <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/10">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                            <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Distinctiveness</p>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-zinc-700/50 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-blue-500"
                                  style={{ width: `${(tell.distinctiveness || 0) * 100}%` }}
                                />
                              </div>
                              <span className="text-[10px] font-mono text-blue-400">
                                {((tell.distinctiveness || 0) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-px h-8 bg-zinc-700/50" />
                          <div className="flex-1">
                            <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Exploitability</p>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-zinc-700/50 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-orange-500"
                                  style={{ width: `${(tell.exploitability || 0) * 100}%` }}
                                />
                              </div>
                              <span className="text-[10px] font-mono text-orange-400">
                                {((tell.exploitability || 0) * 100).toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Individual Tell Feedback */}
                    {!feedback && (
                      <div className="mt-2 pt-2 border-t border-zinc-700/50">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTellFeedback(idx, tell, 'helpful');
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-zinc-700 hover:bg-green-500/20 hover:text-green-400 text-zinc-400 transition-all"
                          >
                            <ThumbsUp className="w-2.5 h-2.5" />
                            Yes
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTellFeedback(idx, tell, 'not_helpful');
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-zinc-700 hover:bg-red-500/20 hover:text-red-400 text-zinc-400 transition-all"
                          >
                            <ThumbsDown className="w-2.5 h-2.5" />
                            No
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTellFeedback(idx, tell, 'dismissed');
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-500 transition-all ml-auto"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

// =============================================================================
// EXPORT ACTIONS COMPONENT
// =============================================================================

function ExportActions({ pitcherData, pitcherName }) {
  const [showMenu, setShowMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const handleExportJSON = () => {
    const data = {
      pitcher: pitcherName,
      exportDate: new Date().toISOString(),
      ...pitcherData
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `the-wire-${pitcherName.toLowerCase()}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowMenu(false);
  };

  const handleExportPDF = async () => {
    setPdfGenerating(true);
    setShowMenu(false);

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (height = 30) => {
        if (yPos + height > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Load and add SLAM logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = '/SLAM_logo.png';

      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => resolve(); // Continue even if logo fails
      });

      // Header with SLAM logo
      if (logoImg.complete && logoImg.naturalWidth > 0) {
        const logoWidth = 50;
        const logoHeight = (logoImg.naturalHeight / logoImg.naturalWidth) * logoWidth;
        const logoX = (pageWidth - logoWidth) / 2;
        doc.addImage(logoImg, 'PNG', logoX, yPos, logoWidth, Math.min(logoHeight, 20));
        yPos += Math.min(logoHeight, 20) + 8;
      }

      // Title
      doc.setFontSize(24);
      doc.setTextColor(255, 100, 0); // Orange
      doc.text('THE WIRE', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      doc.setFontSize(12);
      doc.setTextColor(150, 150, 150);
      doc.text('ADVANCED SCOUTING INTELLIGENCE REPORT', pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;

      // Pitcher Info
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(30, 30, 30);
      doc.roundedRect(margin, yPos, pageWidth - margin * 2, 20, 3, 3, 'F');
      doc.text(pitcherName, pageWidth / 2, yPos + 13, { align: 'center' });
      yPos += 28;

      // Summary Stats Box
      const tippingRate = ((pitcherData.tippingRate || 0) * 100).toFixed(1);
      const totalPitches = pitcherData.totalPitches || 0;
      const criticalTellsCount = pitcherData.criticalTells?.length || 0;

      doc.setFontSize(11);
      doc.setTextColor(200, 200, 200);
      doc.text('SUMMARY STATISTICS', margin, yPos);
      yPos += 6;

      doc.setFillColor(40, 40, 40);
      doc.roundedRect(margin, yPos, pageWidth - margin * 2, 25, 2, 2, 'F');

      // Stats row
      const statsStartX = margin + 10;
      const statsWidth = (pageWidth - margin * 2 - 20) / 4;

      doc.setFontSize(16);
      doc.setTextColor(255, 100, 0);
      doc.text(`${tippingRate}%`, statsStartX, yPos + 10);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('TIPPING RATE', statsStartX, yPos + 17);

      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(`${totalPitches}`, statsStartX + statsWidth, yPos + 10);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('PITCHES ANALYZED', statsStartX + statsWidth, yPos + 17);

      doc.setFontSize(16);
      doc.setTextColor(239, 68, 68); // Red
      doc.text(`${criticalTellsCount}`, statsStartX + statsWidth * 2, yPos + 10);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('CRITICAL TELLS', statsStartX + statsWidth * 2, yPos + 17);

      doc.setFontSize(16);
      doc.setTextColor(34, 197, 94); // Green
      doc.text(`${pitcherData.analyses?.filter(a => !a.tippingDetected).length || 0}`, statsStartX + statsWidth * 3, yPos + 10);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('CLEAN PITCHES', statsStartX + statsWidth * 3, yPos + 17);

      yPos += 32;

      // Critical Tells Section
      if (pitcherData.criticalTells?.length > 0) {
        checkPageBreak(40);
        doc.setFontSize(11);
        doc.setTextColor(255, 100, 0);
        doc.text('CRITICAL TELLS', margin, yPos);
        yPos += 6;

        pitcherData.criticalTells.slice(0, 8).forEach((tell, idx) => {
          checkPageBreak(20);
          doc.setFillColor(35, 35, 35);
          doc.roundedRect(margin, yPos, pageWidth - margin * 2, 16, 2, 2, 'F');

          doc.setFontSize(10);
          doc.setTextColor(255, 255, 255);
          doc.text(`${idx + 1}. ${tell.tell || 'Unknown tell'}`, margin + 4, yPos + 6);

          doc.setFontSize(8);
          doc.setTextColor(255, 100, 0);
          const confidence = ((tell.confidence || 0) * 100).toFixed(0);
          doc.text(`${confidence}% confidence`, pageWidth - margin - 30, yPos + 6);

          if (tell.pitchTypesAffected?.length > 0) {
            doc.setTextColor(150, 150, 150);
            doc.text(`Affects: ${tell.pitchTypesAffected.join(', ')}`, margin + 4, yPos + 12);
          }

          yPos += 18;
        });
      }

      // Pitch Type Breakdown
      if (pitcherData.byPitchType && Object.keys(pitcherData.byPitchType).length > 0) {
        checkPageBreak(40);
        yPos += 4;
        doc.setFontSize(11);
        doc.setTextColor(59, 130, 246); // Blue
        doc.text('PITCH ARSENAL BREAKDOWN', margin, yPos);
        yPos += 6;

        Object.entries(pitcherData.byPitchType).forEach(([type, stats]) => {
          checkPageBreak(14);
          doc.setFillColor(35, 35, 35);
          doc.roundedRect(margin, yPos, pageWidth - margin * 2, 12, 2, 2, 'F');

          doc.setFontSize(10);
          doc.setTextColor(255, 255, 255);
          const label = PITCH_TYPE_LABELS[type] || type;
          doc.text(`${label} (${type})`, margin + 4, yPos + 8);

          const pitchTippingRate = ((stats.tippingRate || 0) * 100).toFixed(0);
          const tippingColor = pitchTippingRate > 50 ? [239, 68, 68] : pitchTippingRate > 25 ? [249, 115, 22] : [34, 197, 94];
          doc.setTextColor(...tippingColor);
          doc.text(`${pitchTippingRate}% tipping`, pageWidth - margin - 35, yPos + 8);

          doc.setTextColor(150, 150, 150);
          doc.text(`${stats.totalCount || stats.count || 0} pitches`, pageWidth - margin - 70, yPos + 8);

          yPos += 14;
        });
      }

      // Recommendations
      if (pitcherData.recommendations?.length > 0) {
        checkPageBreak(40);
        yPos += 4;
        doc.setFontSize(11);
        doc.setTextColor(34, 197, 94); // Green
        doc.text('STRATEGIC RECOMMENDATIONS', margin, yPos);
        yPos += 6;

        pitcherData.recommendations.slice(0, 5).forEach((rec, idx) => {
          checkPageBreak(20);
          doc.setFillColor(35, 35, 35);

          // Calculate text height
          const textLines = doc.splitTextToSize(rec, pageWidth - margin * 2 - 12);
          const boxHeight = Math.max(12, textLines.length * 5 + 6);

          doc.roundedRect(margin, yPos, pageWidth - margin * 2, boxHeight, 2, 2, 'F');

          doc.setFontSize(9);
          doc.setTextColor(255, 255, 255);
          doc.text(`${idx + 1}.`, margin + 4, yPos + 6);
          doc.text(textLines, margin + 10, yPos + 6);

          yPos += boxHeight + 2;
        });
      }

      // Footer
      checkPageBreak(20);
      yPos = pageHeight - 15;
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text('Generated by SLAM Sports AI - THE WIRE Intelligence Platform', pageWidth / 2, yPos, { align: 'center' });
      doc.text(`Report Date: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPos + 4, { align: 'center' });

      // Save PDF
      doc.save(`the-wire-${pitcherName.toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    setShowMenu(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `THE WIRE - ${pitcherName} Analysis`,
          text: `Advanced scouting intelligence for ${pitcherName}`,
          url: window.location.href
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: copy link
      handleCopyLink();
    }
    setShowMenu(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={pdfGenerating}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 rounded-lg transition-all text-sm font-medium disabled:opacity-50"
      >
        {pdfGenerating ? (
          <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {pdfGenerating ? 'Generating...' : 'Export'}
        <ChevronDown className={`w-3 h-3 transition-transform ${showMenu ? 'rotate-180' : ''}`} />
      </button>

      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
          <button
            onClick={handleExportPDF}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors text-left"
          >
            <FileText className="w-4 h-4 text-red-400" />
            Download PDF Report
          </button>
          <div className="border-t border-zinc-700" />
          <button
            onClick={handleExportJSON}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors text-left"
          >
            <FileDown className="w-4 h-4 text-blue-400" />
            Download JSON Data
          </button>
          <div className="border-t border-zinc-700" />
          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors text-left"
          >
            <Copy className="w-4 h-4 text-purple-400" />
            {copySuccess ? 'Copied!' : 'Copy Report Link'}
          </button>
          <div className="border-t border-zinc-700" />
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors text-left"
          >
            <Share2 className="w-4 h-4 text-green-400" />
            Share Report
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// RECOMMENDATIONS PANEL
// =============================================================================

function RecommendationsPanel({ recommendations, viewMode }) {
  const isCoachMode = viewMode === 'coach';

  return (
    <div className={`relative rounded-2xl overflow-hidden backdrop-blur-md ${
      isCoachMode
        ? 'border border-blue-500/20 bg-gradient-to-br from-zinc-800/60 to-zinc-900/60'
        : 'border border-zinc-700 bg-zinc-800/30'
    }`}>
      {/* Coach mode ambient glow */}
      {isCoachMode && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />
      )}

      <div className="relative p-4">
        <h3 className={`text-sm font-bold mb-3 flex items-center gap-2 ${
          isCoachMode ? 'text-blue-400' : 'text-white'
        }`}>
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            isCoachMode ? 'bg-blue-500/20 ring-1 ring-blue-500/30' : 'bg-zinc-700/50'
          }`}>
            <Target className="w-4 h-4 text-blue-400" />
          </div>
          {isCoachMode ? 'Coaching Intel' : 'At-Bat Strategy'}
        </h3>

        <div className="space-y-2 max-h-[240px] lg:max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
          {recommendations.map((rec, idx) => (
            <div key={idx} className={`flex items-start gap-2 p-3 rounded-xl transition-all ${
              isCoachMode
                ? 'bg-zinc-800/40 border border-white/5 hover:border-blue-500/30'
                : 'bg-zinc-800/50'
            }`}>
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isCoachMode ? 'bg-blue-500/20 ring-1 ring-blue-500/20' : 'bg-blue-500/20'
              }`}>
                <span className="text-[10px] text-blue-400 font-bold">{idx + 1}</span>
              </div>
              <p className="text-[11px] text-zinc-300 leading-relaxed">{rec}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function TheWireReport({ sourceId = 'wake-forest' }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pitcherDetail, setPitcherDetail] = useState(null);
  const [selectedPitcher, setSelectedPitcher] = useState(null);
  const [viewMode, setViewMode] = useState('hitter');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('intelligence'); // 'intelligence' | 'evaluation'

  // Intel source from prop (wake-forest by default, tigers for /tipping)
  const intelSourceId = sourceId;

  // Header is always sticky (no scroll-hide behavior)

  // Load summary when intel source changes
  useEffect(() => {
    async function fetchSummary() {
      try {
        setLoading(true);
        const data = await loadWireSummary(intelSourceId);
        setSummary(data);
        // Set first pitcher as selected
        if (data.pitchers && data.pitchers.length > 0) {
          setSelectedPitcher(data.pitchers[0]);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to load summary:', err);
        setError('Failed to load intelligence data');
        setLoading(false);
      }
    }
    fetchSummary();
  }, [intelSourceId]);

  // Load pitcher detail when selection or source changes
  useEffect(() => {
    async function fetchPitcherDetail() {
      if (!selectedPitcher) return;
      try {
        setLoading(true);
        const data = await loadPitcherDetail(selectedPitcher, intelSourceId);
        setPitcherDetail(data);
        setError(null);
      } catch (err) {
        console.error(`Failed to load ${selectedPitcher}:`, err);
        setError(`Failed to load ${selectedPitcher} data`);
      } finally {
        setLoading(false);
      }
    }
    fetchPitcherDetail();
  }, [selectedPitcher, intelSourceId]);

  // Generate hitter report
  const hitterReport = pitcherDetail ? generateHitterScoutingReport(pitcherDetail) : null;

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400 font-mono text-sm">Loading intelligence...</p>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 font-mono mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header - 2026 Glass Morphism Design - Always Sticky */}
      <header className="border-b border-white/5 bg-zinc-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center justify-center w-10 h-10 rounded-xl bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700/50 transition-all hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 text-zinc-400" />
              </button>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center p-1.5 ring-1 ring-white/10">
                  <img
                    src={INTEL_SOURCES[intelSourceId]?.logo || '/logos/wake-forest.png'}
                    alt={INTEL_SOURCES[intelSourceId]?.name || 'Team'}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-bold text-white tracking-tight">THE WIRE</h1>
                    <span className="px-1.5 py-0.5 text-[9px] font-bold bg-orange-500/20 text-orange-400 rounded uppercase tracking-wider">
                      Intel
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-500">{INTEL_SOURCES[intelSourceId]?.name || 'Advanced Scouting Intelligence'}</p>
                </div>
              </div>

              {/* Quick Stats - Redesigned */}
              {summary && (
                <div className="hidden lg:flex items-center gap-1 ml-6 pl-6 border-l border-zinc-800">
                  <div className="px-3 py-1.5 bg-zinc-800/50 rounded-lg border border-zinc-700/30">
                    <p className="text-lg font-bold text-white font-mono leading-none">{summary.totalVideos}</p>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Analyzed</p>
                  </div>
                  <div className="px-3 py-1.5 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <p className="text-lg font-bold font-mono text-orange-400 leading-none">
                      {(summary.overallTippingRate * 100).toFixed(0)}%
                    </p>
                    <p className="text-[9px] text-orange-400/70 uppercase tracking-wider">Tipping</p>
                  </div>
                  <div className="px-3 py-1.5 bg-zinc-800/50 rounded-lg border border-zinc-700/30">
                    <p className="text-lg font-bold text-white font-mono leading-none">{summary.pitchers?.length || 1}</p>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Pitchers</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <ViewToggle value={viewMode} onChange={setViewMode} />
              {pitcherDetail && (
                <ExportActions pitcherData={pitcherDetail} pitcherName={selectedPitcher} />
              )}
              {/* AI Chat Toggle */}
              <button
                onClick={() => setIsChatOpen(!isChatOpen)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-sm font-medium ${
                  isChatOpen
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                    : 'bg-zinc-800/80 border-zinc-700/50 text-zinc-300 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Ask AI</span>
              </button>
              <button
                onClick={() => navigate('/video-analytics?tab=moe&mode=pitch-tipping')}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all text-sm font-medium shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:scale-105"
              >
                <Play className="w-4 h-4" fill="white" />
                Analyze More
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 sticky top-[73px] z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('intelligence')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === 'intelligence'
                  ? 'border-orange-500 text-orange-400 bg-orange-500/5'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
              }`}
            >
              <Target className="w-4 h-4" />
              Pitch Intelligence
            </button>
            <button
              onClick={() => setActiveTab('evaluation')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === 'evaluation'
                  ? 'border-purple-500 text-purple-400 bg-purple-500/5'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
              }`}
            >
              <Brain className="w-4 h-4" />
              Eval of SLAM AI
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-500/20 text-purple-400 rounded uppercase">
                New
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-4">
        {/* AI Evaluation Tab */}
        {activeTab === 'evaluation' && (
          <SlamAIEvaluation
            analyses={pitcherDetail?.analyses || []}
          />
        )}

        {/* Intelligence Tab */}
        {activeTab === 'intelligence' && (
          <>
            {/* Pitcher Selector */}
            {summary && summary.pitchers.length > 1 && (
              <div className="mb-4">
                <PitcherSelector
                  pitchers={summary.pitchers}
                  selected={selectedPitcher}
                  onSelect={setSelectedPitcher}
                  summaryData={summary.byPitcher}
                />
              </div>
            )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Pitcher Content */}
        {pitcherDetail && !loading && (
          <div className="space-y-4">
            {/* Summary Card */}
            <PitcherSummaryCard
              pitcher={selectedPitcher}
              summary={pitcherDetail}
              viewMode={viewMode}
            />

            {/* Hitter Intelligence Briefing - Premium Synthesized Intel */}
            {viewMode === 'hitter' && pitcherDetail.opponentIntel?.threatAssessment && (
              <HitterIntelBriefing pitcherData={pitcherDetail.opponentIntel} />
            )}

            {/* Two Column Layout - 2026 Bento Style */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-4">
                {/* Pitch Type Breakdown with analyses for zone data */}
                <PitchTypeBreakdown
                  byPitchType={pitcherDetail.byPitchType}
                  viewMode={viewMode}
                  analyses={pitcherDetail.analyses}
                />

                {/* Tell Evidence Carousel - Videos grouped by tell type */}
                {pitcherDetail.analyses?.length > 0 && (
                  <BentoCard className="p-4">
                    <TellCarousel
                      analyses={pitcherDetail.analyses}
                      pitcherName={selectedPitcher}
                    />
                  </BentoCard>
                )}

                {/* Video Gallery */}
                {pitcherDetail.analyses?.length > 0 && (
                  <VideoGallery
                    analyses={pitcherDetail.analyses}
                    maxDisplay={8}
                    pageSize={12}
                    hasPublicVideos={INTEL_SOURCES[intelSourceId]?.hasPublicVideos ?? true}
                    pitcherName={selectedPitcher}
                  />
                )}

                {/* Color-Coded Metrics Table (Coach View) */}
                {viewMode === 'coach' && pitcherDetail.criticalTells?.length > 0 && (
                  <ColorCodedMetricsTable
                    title="Detailed Tell Analysis"
                    data={pitcherDetail.criticalTells.map(tell => ({
                      category: tell.pitchTypesAffected?.length > 1 ? 'Multi-Pitch' : (tell.pitchTypesAffected?.[0] || 'General'),
                      subCategory: tell.tell,
                      score: (tell.confidence || 0) * 100,
                      description: tell.description || `Occurs ${tell.occurrences || 1} times`,
                      inverted: true,
                    }))}
                    invertedScores={true}
                  />
                )}
              </div>

              {/* Right Column - Tells & Recommendations */}
              <div className="space-y-4">
                {/* Critical Tells - with individual feedback per tell */}
                <CriticalTellsPanel
                  criticalTells={pitcherDetail.criticalTells}
                  viewMode={viewMode}
                  onTellFeedback={(feedback) => {
                    console.log('Tell feedback submitted:', feedback);
                    // TODO: Send to backend - if not_helpful, omit from future analysis
                    // POST /api/wire/tell-feedback
                    // { tellId, feedback, omitFromFuture, timestamp }
                  }}
                />

                {/* Recommendations */}
                <RecommendationsPanel
                  recommendations={pitcherDetail.recommendations}
                  viewMode={viewMode}
                />

                {/* Analysis Summary - Compact 2026 Style */}
                <GlassCard className="p-3" opacity={40}>
                  <h3 className="text-xs font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wider">
                    <Eye className="w-3.5 h-3.5 text-purple-400" />
                    Summary
                  </h3>

                  {/* Compact visual with inline stats */}
                  <div className="flex items-center gap-3 mb-3">
                    {/* Mini donut */}
                    <div className="relative w-14 h-14 flex-shrink-0">
                      <svg className="w-14 h-14 transform -rotate-90">
                        <circle cx="28" cy="28" r="24" stroke="#3f3f46" strokeWidth="4" fill="none" />
                        <circle
                          cx="28" cy="28" r="24"
                          stroke="#22c55e" strokeWidth="4" fill="none"
                          strokeDasharray={`${((pitcherDetail.analyses?.filter(a => !a.tippingDetected).length || 0) / (pitcherDetail.analyses?.length || 1)) * 151} 151`}
                        />
                        <circle
                          cx="28" cy="28" r="24"
                          stroke="#f97316" strokeWidth="4" fill="none"
                          strokeDasharray={`${((pitcherDetail.analyses?.filter(a => a.tippingDetected).length || 0) / (pitcherDetail.analyses?.length || 1)) * 151} 151`}
                          strokeDashoffset={`${-((pitcherDetail.analyses?.filter(a => !a.tippingDetected).length || 0) / (pitcherDetail.analyses?.length || 1)) * 151}`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-bold font-mono text-white">
                          {pitcherDetail.analyses?.length || 0}
                        </span>
                      </div>
                    </div>

                    {/* Inline stats */}
                    <div className="flex-1 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-orange-500" />
                          <span className="text-[10px] text-zinc-400">Tipping</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-orange-400">
                          {pitcherDetail.analyses?.filter(a => a.tippingDetected).length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-[10px] text-zinc-400">Clean</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-green-400">
                          {pitcherDetail.analyses?.filter(a => !a.tippingDetected).length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-zinc-700/50">
                        <span className="text-[10px] text-zinc-500">Updated</span>
                        <span className="text-[10px] font-mono text-zinc-400">
                          {new Date(pitcherDetail.lastUpdated).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 bg-zinc-900 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between text-xs text-zinc-600">
            <p>THE WIRE - SLAM Sports AI Intelligence Platform</p>
            <p>
              {pitcherDetail && `Last updated: ${new Date(pitcherDetail.lastUpdated).toLocaleString()}`}
            </p>
          </div>
        </div>
      </footer>

      {/* AI Chat Panel */}
      {pitcherDetail && (
        <PitcherChat
          pitcherData={{
            ...pitcherDetail,
            // Spread opponentIntel to root level for chat system prompt
            ...pitcherDetail.opponentIntel,
          }}
          pitcherName={selectedPitcher}
          isOpen={isChatOpen}
          onToggle={() => setIsChatOpen(!isChatOpen)}
        />
      )}
    </div>
  );
}
